/**
 * Customer profile (spec §2.4 P4): outstanding balance, credit limit with a
 * warning when it is exceeded (never a block, OQ-INV-7), the customer's
 * invoices, and the statement.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ClientRepository from '../../../internals/repository/ClientRepository';
import InvoiceRepository, { CustomerBalance, InvoiceListItem } from '../../../internals/repository/InvoiceRepository';
import StatementDialog from '../../invoice/components/StatementDialog';
import StatusChip from '../../invoice/components/StatusChip';
import { ErrorBanner } from '../../invoice/components/VoidModal';
import { errorText } from '../../invoice/detailModel';
import { date, money, moneyInput, parseMoney } from '../../invoice/format';
import { creditWarning } from '../../invoice/posModel';
import { useSelectedShop } from '../../invoice/useInvoicing';

const shell = 'h-screen flex flex-col w-full p-5 md:px-8 md:py-7 overflow-auto [&>*]:shrink-0 gap-5';
const capsCls = 'text-[11px] font-semibold uppercase tracking-[.07em] text-text3';
const GRID = 'minmax(130px,1fr) 110px 110px 150px 130px 130px';

type Tab = 'invoices' | 'details';

function useClient(shopId: string | undefined, clientId: string | undefined) {
  const [client, setClient] = useState<any>(null);
  useEffect(() => {
    if (!shopId || !clientId) return undefined;
    let live = true;
    (async () => {
      // The list API has no single-customer read; page through until it turns up.
      let lastKey: any = null;
      for (let i = 0; i < 20 && live; i += 1) {
        const res: any = await ClientRepository.fetchClients(shopId, 100, lastKey);
        const found = (res?.items || []).find((c: any) => c.id === clientId);
        if (found) { if (live) setClient(found); return; }
        lastKey = res?.lastKey ?? null;
        if (!lastKey || Object.keys(lastKey).length === 0) break;
      }
      if (live) setClient(false);
    })();
    return () => { live = false; };
  }, [shopId, clientId]);
  return client;
}

export default function CustomerDetailView() {
  const { clientId = '' } = useParams();
  const navigate = useNavigate();
  const shop = useSelectedShop();
  const shopId: string | undefined = shop?.shopId;
  const cur = shop?.currency || 'LKR';
  const client = useClient(shopId, clientId);
  const [tab, setTab] = useState<Tab>('invoices');
  const [balance, setBalance] = useState<CustomerBalance | null>(null);
  const [invoices, setInvoices] = useState<InvoiceListItem[] | null>(null);
  const [loadError, setLoadError] = useState<any>(null);
  const [limitText, setLimitText] = useState('');
  const [saving, setSaving] = useState(false);
  const [limitError, setLimitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [statement, setStatement] = useState(false);

  const load = useCallback(async () => {
    if (!shopId || !clientId) return;
    setLoadError(null);
    try {
      const [b, list] = await Promise.all([
        InvoiceRepository.customerBalance(shopId, clientId),
        InvoiceRepository.list(shopId, { clientId, limit: 100 }),
      ]);
      setBalance(b);
      setLimitText(b.creditLimitMinor == null ? '' : moneyInput(b.creditLimitMinor));
      setInvoices(list.items);
    } catch (e) {
      setLoadError(e);
      setInvoices([]);
    }
  }, [shopId, clientId]);

  useEffect(() => { load(); }, [load]);

  const name = client ? `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || client.name : '';

  const saveLimit = async () => {
    if (!shopId) return;
    const text = limitText.trim();
    const value = text === '' ? null : parseMoney(text);
    if (value !== null && (!Number.isFinite(value) || value < 0)) { setLimitError('Enter an amount, or leave it empty for no limit.'); return; }
    setSaving(true);
    setLimitError(null);
    try {
      const b = await InvoiceRepository.setCreditLimit(shopId, clientId, value);
      setBalance(b);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      setLimitError(errorText(e, { forbidden_role: 'Only the shop owner or an admin can change the credit limit.' }));
    } finally {
      setSaving(false);
    }
  };

  if (!shopId) return <div className={shell}><p className="text-text2">Select a shop to see this customer.</p></div>;

  const warn = creditWarning(balance, cur);
  const cards = [
    { label: 'Outstanding', value: balance ? money(balance.outstandingMinor, cur) : '—', tone: balance && balance.outstandingMinor > 0 ? 'var(--text)' : 'var(--text3)' },
    { label: 'Overdue', value: balance ? money(balance.overdueMinor, cur) : '—', tone: balance && balance.overdueMinor > 0 ? 'var(--negFg)' : 'var(--text3)' },
    { label: 'Open invoices', value: balance ? String(balance.openCount) : '—', tone: 'var(--text)' },
    { label: 'Credit limit', value: balance ? (balance.creditLimitMinor == null ? 'No limit' : money(balance.creditLimitMinor, cur)) : '—', tone: 'var(--text)' },
  ];

  return (
    <div className={shell}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <Link to="/customers" className="text-[13px] text-text2 hover:text-brand">← Customers</Link>
          <h1 className="m-0 text-[28px] font-semibold tracking-[-0.015em] text-text1">{client === null ? 'Customer' : name || 'Customer'}</h1>
          <span className="text-[14px] text-text2">
            {[client?.mobileNumber && client.mobileNumber.length > 7 ? client.mobileNumber : null, client?.email].filter(Boolean).join(' · ') || ' '}
          </span>
        </div>
        <div className="flex gap-2">
          <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setStatement(true)}>Statement</button>
          <button type="button" className="nl-btn nl-btn--primary" onClick={() => navigate(`/invoices/new?clientId=${encodeURIComponent(clientId)}`)}>New invoice</button>
        </div>
      </header>

      {client === false && <ErrorBanner text="This customer was not found. They may have been deleted." />}

      <div className="grid max-w-[1360px] grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="nl-card flex flex-col gap-1 px-4 py-3.5">
            <span className={capsCls}>{c.label}</span>
            <span className="nl-num text-[20px] font-semibold tabular-nums" style={{ color: c.tone }}>{c.value}</span>
          </div>
        ))}
      </div>

      {warn && (
        <div role="status" className="max-w-[1360px] rounded-lg border border-warn/40 bg-warn-soft px-3.5 py-2.5 text-[13px]" style={{ color: 'var(--warnFg)' }}>
          {warn} New invoices and sales on account still go through.
        </div>
      )}

      <div className="flex max-w-[1360px] gap-1 border-b border-borderc" role="tablist">
        {(['invoices', 'details'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-4 pb-3 text-sm font-semibold ${tab === t ? 'border-brand text-brand' : 'border-transparent text-text2 hover:text-text1'}`}
          >
            {t === 'invoices' ? `Invoices${invoices ? ` (${invoices.length})` : ''}` : 'Details'}
          </button>
        ))}
      </div>

      {tab === 'invoices' && (
        <div className="nl-card max-w-[1360px] overflow-x-auto p-0">
          <div className="min-w-[760px]" role="table" aria-label="Customer invoices">
            <div role="row" className={`grid items-center gap-3 bg-surface2 px-[18px] py-2.5 ${capsCls}`} style={{ gridTemplateColumns: GRID }}>
              <span role="columnheader">Number</span>
              <span role="columnheader">Issued</span>
              <span role="columnheader">Due</span>
              <span role="columnheader">Status</span>
              <span role="columnheader" className="text-right">Total</span>
              <span role="columnheader" className="text-right">Balance</span>
            </div>
            {invoices === null && [0, 1, 2].map((i) => (
              <div key={i} className="border-t border-borderc px-[18px] py-5" aria-hidden>
                <span className="block h-2.5 w-1/2 animate-pulse rounded bg-surface3" />
              </div>
            ))}
            {loadError && (
              <div className="flex flex-col items-center gap-2 border-t border-borderc px-5 py-10 text-center">
                <span className="font-semibold text-text1">Invoices could not load</span>
                <span className="text-[13px] text-text2">{errorText(loadError)}</span>
                <button type="button" className="nl-btn nl-btn--sm nl-btn--secondary" onClick={load}>Try again</button>
              </div>
            )}
            {invoices && !loadError && invoices.length === 0 && (
              <div className="flex flex-col items-center gap-1.5 border-t border-borderc px-5 py-12 text-center">
                <span className="text-[16px] font-semibold text-text1">No invoices yet</span>
                <span className="max-w-[420px] text-text2">Invoices for this customer, including sales sent as invoices from Receipts, show here.</span>
              </div>
            )}
            {invoices && invoices.map((d) => (
              <Link
                key={d.docId}
                to={`/invoices/${encodeURIComponent(d.docId)}`}
                role="row"
                className="grid items-center gap-3 border-t border-borderc px-[18px] py-[13px] text-text1 hover:bg-surface2"
                style={{ gridTemplateColumns: GRID }}
              >
                <span role="cell" className="truncate font-semibold">{d.number ?? 'Draft'}</span>
                <span role="cell" className="text-[13px] text-text2">{date(d.issueDate)}</span>
                <span role="cell" className="text-[13px] text-text2">{d.dueDate ? date(d.dueDate) : '—'}</span>
                <span role="cell"><StatusChip status={d.displayStatus} /></span>
                <span role="cell" className="nl-num text-right tabular-nums">{money(d.totalMinor, cur)}</span>
                <span role="cell" className="nl-num text-right font-semibold tabular-nums">{money(d.balanceMinor, cur)}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {tab === 'details' && (
        <div className="nl-card flex max-w-[640px] flex-col gap-4 px-5 py-4">
          <dl className="m-0 grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 text-[14px]">
            <dt className="text-text2">Name</dt><dd className="m-0 text-text1">{name || '—'}</dd>
            <dt className="text-text2">Phone</dt><dd className="m-0 text-text1">{client?.mobileNumber && client.mobileNumber.length > 7 ? client.mobileNumber : '—'}</dd>
            <dt className="text-text2">Email</dt><dd className="m-0 text-text1">{client?.email || '—'}</dd>
            <dt className="text-text2">Address</dt><dd className="m-0 text-text1">{client?.address || '—'}</dd>
          </dl>
          <form className="flex flex-col gap-1.5 border-t border-borderc pt-4" onSubmit={(e) => { e.preventDefault(); saveLimit(); }}>
            <label htmlFor="credit-limit" className="text-[13px] font-semibold text-text2">Credit limit</label>
            <div className="flex gap-2">
              <input
                id="credit-limit"
                className="nl-input max-w-[220px]"
                inputMode="decimal"
                placeholder="No limit"
                value={limitText}
                onChange={(e) => { setLimitText(e.target.value); setLimitError(null); }}
              />
              <button type="submit" className="nl-btn nl-btn--secondary" disabled={saving}>{saving ? 'Saving…' : saved ? 'Saved' : 'Save'}</button>
            </div>
            <span className="text-[12px] text-text3">Going over the limit shows a warning. Sales and invoices are never blocked.</span>
            <ErrorBanner text={limitError} />
          </form>
        </div>
      )}

      <StatementDialog
        open={statement}
        onClose={() => setStatement(false)}
        shopId={shopId}
        clientId={clientId}
        customerName={name}
        phone={client?.mobileNumber}
        email={client?.email}
      />
    </div>
  );
}
